import { defineStore } from "pinia";
import { ref, nextTick } from "vue";
import { getChat } from "~/services/chatService";

export const useChatStore = defineStore("chat-store", () => {
	const hasNewMessage = ref(false);
	const currentMessageData = ref<any | null>(null);
	const refetchChat = ref(false);
	const chatMessagesWidget = ref<any[]>([]);
	const unreadCount = ref(0);
	const pollingIntervals: Record<string, number> = {};

	const callNewMessage = (messageData: any) => {
		Promise.resolve().then(() => {
			hasNewMessage.value = true;
			currentMessageData.value = messageData;

			const index = chatMessagesWidget.value.findIndex((chat) => chat.data.id === messageData.chat_id);
			if (index !== -1) {
				if (!chatMessagesWidget.value[index].data.chat_messages) {
					chatMessagesWidget.value[index].data.chat_messages = [];
				}

				const messageExists = chatMessagesWidget.value[index].data.chat_messages.some(
					(msg: any) => msg.id === messageData.message_object.id,
				);

				if (!messageExists) {
					chatMessagesWidget.value[index].data.chat_messages.push(messageData.message_object);
					chatMessagesWidget.value = [...chatMessagesWidget.value];
					nextTick(() => {
						refetchChat.value = true;
					});
				}
			}
		});
	};

	const startPollingForMessages = (chatId: string | number, interval = 3000) => {
		const pollingId = `polling-${chatId}`;

		if (pollingIntervals[pollingId]) {
			clearInterval(pollingIntervals[pollingId]);
		}

		pollingIntervals[pollingId] = window.setInterval(async () => {
			try {
				const isWebSocketConnected = window.Echo?.connector?.pusher?.connection?.state === "connected";

				if (isWebSocketConnected) {
					clearInterval(pollingIntervals[pollingId]);
					delete pollingIntervals[pollingId];
					return;
				}

				await handleChatMessages(chatId);
			} catch {
				// polling failure — will retry on next interval
			}
		}, interval);
	};

	const readedNewMessage = () => {
		hasNewMessage.value = false;
	};

	const getRefetchChat = () => {
		refetchChat.value = true;
	};

	const resetRefetchChat = () => {
		refetchChat.value = false;
	};

	const addLoadingChat = (chatId: string | number, users: any[] = []) => {
		const index = chatMessagesWidget.value.findIndex((c: any) => c?.data?.id === chatId);
		if (index === -1) {
			chatMessagesWidget.value.push({ data: { id: chatId, chat_messages: null, users } } as any);
		}
	};

	const setChatMessagesWidget = (data: any, refresh: boolean = false) => {
		const index = chatMessagesWidget.value.findIndex((chat) => chat.data.id === data.data.id);
		if (index === -1) {
			chatMessagesWidget.value.push(data);
		} else if (refresh) {
			chatMessagesWidget.value[index] = data;
		}
	};

	const handleChatMessages = async (chatId: string | number) => {
		try {
			const response = await getChat({ chat_id: chatId });
			setChatMessagesWidget(response.data, true);

			const isWebSocketConnected = window.Echo?.connector?.pusher?.connection?.state === "connected";
			if (!isWebSocketConnected) {
				startPollingForMessages(chatId);
			}
		} catch {
			// polling failure — will retry
		}
	};

	const stopAllPolling = () => {
		Object.values(pollingIntervals).forEach(clearInterval);
		Object.keys(pollingIntervals).forEach((key) => delete pollingIntervals[key]);
	};

	const removeChatFromWidgetList = (chatId: string | number) => {
		chatMessagesWidget.value = chatMessagesWidget.value.filter((chat) => chat.data.id !== chatId);
	};

	const newChat = (data: any) => {
		setChatMessagesWidget(data);
	};

	const substituteUserChat = (chatId: string | number, userId: string | number) => {
		chatMessagesWidget.value = chatMessagesWidget.value.map((chat) => {
			if (chat.data.id === userId) {
				return { ...chat, data: { ...chat.data, id: chatId } };
			}
			return chat;
		});
		handleChatMessages(chatId);
	};

	return {
		hasNewMessage,
		currentMessageData,
		callNewMessage,
		readedNewMessage,
		refetchChat,
		resetRefetchChat,
		getRefetchChat,
		addLoadingChat,
		setChatMessagesWidget,
		chatMessagesWidget,
		handleChatMessages,
		removeChatFromWidgetList,
		newChat,
		substituteUserChat,
		startPollingForMessages,
		stopAllPolling,
		unreadCount,
	};
});

if (import.meta.hot) {
	import.meta.hot.accept(acceptHMRUpdate(useChatStore, import.meta.hot));
}
